import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import AppShell from "../components/AppShell";
import api from "../lib/api";
import { useBilling } from "../context/BillingContext";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh } = useBilling();
  const [state, setState] = useState("checking");
  const [plan, setPlan] = useState("");

  useEffect(() => {
    let tries = 0, stop = false;
    const poll = async () => {
      if (stop) return;
      try {
        const { data } = await api.get(`/billing/checkout/status/${sessionId}`);
        setPlan(data.plan);
        if (data.payment_status === "paid") { setState("paid"); refresh(); return; }
        if (data.status === "expired") { setState("failed"); return; }
      } catch { setState("failed"); return; }
      if (++tries >= 15) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
    // eslint-disable-next-line
  }, [sessionId]);

  return (
    <AppShell eyebrow="Billing" title="Checkout">
      <div className="border border-zinc-200 p-8 max-w-xl" data-testid="payment-result">
        {state === "checking" && <div className="flex items-center gap-3 text-sm" data-testid="payment-checking"><Loader2 className="w-5 h-5 animate-spin text-[#0033FF]" /> Confirming your payment…</div>}
        {state === "paid" && <>
          <CheckCircle2 className="w-10 h-10 text-[#0033FF] mb-4" />
          <div className="text-2xl font-black tracking-tighter mb-2" data-testid="payment-success">You're on {plan?.toUpperCase()}.</div>
          <p className="text-sm text-zinc-600 mb-6">Your subscription is active and renews automatically. Everything in your new plan is unlocked.</p>
          <Link to="/dashboard" data-testid="payment-goto-dashboard" className="bg-[#0033FF] text-white px-5 py-3 text-xs font-bold uppercase tracking-wider hover:bg-zinc-900 inline-block">Go to dashboard</Link>
        </>}
        {(state === "failed" || state === "timeout") && <>
          <XCircle className="w-10 h-10 text-[#FF3B30] mb-4" />
          <div className="text-2xl font-black tracking-tighter mb-2" data-testid="payment-failed">{state === "timeout" ? "Still processing" : "Payment not completed"}</div>
          <p className="text-sm text-zinc-600 mb-6">{state === "timeout" ? "We haven't received confirmation yet. Check back on the billing page in a minute." : "No charge was made. You can try again anytime."}</p>
          <Link to="/billing" data-testid="payment-goto-billing" className="border border-zinc-900 px-5 py-3 text-xs font-bold uppercase tracking-wider hover:bg-zinc-900 hover:text-white inline-block">Back to plans</Link>
        </>}
      </div>
    </AppShell>
  );
}

export function PaymentCancel() {
  return (
    <AppShell eyebrow="Billing" title="Checkout canceled">
      <div className="border border-zinc-200 p-8 max-w-xl" data-testid="payment-cancel">
        <p className="text-sm text-zinc-600 mb-6">No charge was made. Your plan hasn't changed.</p>
        <Link to="/billing" data-testid="payment-cancel-back" className="border border-zinc-900 px-5 py-3 text-xs font-bold uppercase tracking-wider hover:bg-zinc-900 hover:text-white inline-block">Back to plans</Link>
      </div>
    </AppShell>
  );
}
